import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { Article, Product, ProductCategory, PublicPack } from '@/types';
import { api } from '@/client/lib/api';
import { useMeta } from '@/client/lib/useMeta';
import { SHOP_SUBJECTS, SHOP_HUB_IMAGE, SHOP_HUB_SHOWS_FINDS, withFinds } from '@/config/site';
import { showsFinds, useFindsLive } from '@/client/lib/useFindsLive';
import { ProductGrid } from '@/components/ProductGrid';
import { PackCard } from '@/components/PackCard';
import { FramedImage } from '@/components/FramedImage';
import { SectionHeader } from '@/components/SectionHeader';
import { Markdown } from '@/components/Markdown';
import { Breadcrumb } from '@/components/Breadcrumb';
import { LoadingState, EmptyState } from '@/components/States';
import { useI18n } from '@/client/i18n/I18nContext';
import { staticPageText } from '@/lib/seo';
import { localizeArticle } from '@/lib/i18n-content';
import { SHOP_EDIT_CATEGORY, shopCategoryArticles } from '@/lib/articles';
import { EditorialCard } from '@/components/ArticleView';
import { FINDS_CATEGORY, FINDS_PATH, findsOf } from '@/lib/catalog/finds';
import { lp } from '@/client/i18n/locale';
import { useLogicalParams } from '@/client/i18n/useLogicalParams';

export function ShopPage() {
  const { category } = useLogicalParams<'category'>('/shop/:category');
  const { t, locale } = useI18n();
  const [products, setProducts] = useState<Product[] | null>(null);
  // The store's available packs (server-priced), shown FIRST in the grid
  // (docs/plan-packs.md). A failed request just shows no packs.
  const [packs, setPacks] = useState<PublicPack[]>([]);
  // «En el Edit»: published articles of the category's Edit category (same selection as the prerender).
  const [editArticles, setEditArticles] = useState<Article[]>([]);
  // Hub card «Hallazgos» only while a find is public and the card is not paused
  // (SHOP_HUB_SHOWS_FINDS; same rule as the prerender).
  const findsLive = showsFinds(useFindsLive());
  const findsShown = SHOP_HUB_SHOWS_FINDS && findsLive;

  const isCat = SHOP_SUBJECTS.some((s) => s.isProductCategory && s.slug === category);
  const meta = isCat
    ? {
        title: t(`shop.${category}.title`),
        eyebrow: t(`shop.${category}.eyebrow`),
        lede: t(`shop.${category}.lede`),
      }
    : undefined;
  // Same <title>/description as the Worker (lib/seo STATIC_PAGES).
  useMeta({
    ...staticPageText(isCat ? `/shop/${category}` : '/shop', locale),
    path: category ? `/shop/${category}` : '/shop',
  });

  useEffect(() => {
    if (!isCat) {
      setProducts(null);
      return;
    }
    setProducts(null);
    setPacks([]);
    let alive = true;
    // Both lists land together, so the packs never push the products down later.
    Promise.all([
      api.products({ category }).catch(() => [] as Product[]),
      api.packs({ category: category as ProductCategory }).catch(() => [] as PublicPack[]),
    ]).then(([list, packList]) => {
      if (!alive) return;
      setPacks(packList.filter((p) => p.available));
      setProducts(list);
    });
    return () => {
      alive = false;
    };
  }, [category, isCat]);

  useEffect(() => {
    setEditArticles([]);
    if (!isCat) return;
    const shopCategory = category as ProductCategory;
    let alive = true;
    api
      .articles(SHOP_EDIT_CATEGORY[shopCategory])
      .then((list) => {
        if (alive) setEditArticles(shopCategoryArticles(list, shopCategory, Date.now()));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [category, isCat]);

  // Hub: /shop (or an unknown category) shows the available sub-shops/subjects.
  if (!isCat) {
    return (
      <div className="container">
        <Breadcrumb
          items={[
            { name: t('common.home'), path: '/' },
            { name: t('nav.shop'), path: '/shop' },
          ]}
        />
        <SectionHeader as="h1" title={t('shop.hubTitle')} lede={t('shop.hubIntro')} />
        <div className="shop-hub" style={{ paddingBottom: 'var(--space-8)' }}>
          {withFinds(SHOP_SUBJECTS, findsShown).map((s) => {
            // No hub photo yet (Hallazgos): the card shows its title instead.
            const hubImage = SHOP_HUB_IMAGE[s.slug];
            return (
            <Link key={s.slug} to={lp(s.href)} className="shop-hub__card">
              <div className="shop-hub__media" aria-hidden>
                {hubImage ? (
                  <FramedImage src={hubImage} alt="" loading="lazy" fallback={t(`shop.${s.slug}.title`)} />
                ) : (
                  t(`shop.${s.slug}.title`)
                )}
              </div>
              <h2 className="shop-hub__title">{t(`shop.${s.slug}.title`)}</h2>
              <span className="link-underline">{t('shop.hubCta')}</span>
            </Link>
            );
          })}
        </div>
      </div>
    );
  }

  // Casa's category page shows the title only — its editorial eyebrow and lede
  // are hidden on-page.
  const hideHeaderText = category === 'casa';

  return (
    <div className="container">
      <Breadcrumb
        items={[
          { name: t('common.home'), path: '/' },
          { name: t('nav.shop'), path: '/shop' },
          ...(meta ? [{ name: meta.title }] : []),
        ]}
      />
      <SectionHeader
        as="h1"
        eyebrow={hideHeaderText ? undefined : (meta?.eyebrow ?? t('shop.allEyebrow'))}
        title={meta?.title ?? t('shop.allTitle')}
        lede={hideHeaderText ? undefined : (meta?.lede ?? t('shop.allLede'))}
      />

      {products === null ? (
        <LoadingState />
      ) : products.length === 0 && packs.length === 0 ? (
        <EmptyState title={t('shop.emptyTitle')} message={t('shop.emptyMsg')} />
      ) : (
        <ProductGrid
          products={products}
          compact
          leading={packs.map((p) => (
            <PackCard key={`pack-${p.id}`} pack={p} />
          ))}
        />
      )}

      {/* Estilo: link to its one-off pieces, only when there is one (same rule and text as the prerender's teaser). */}
      {category === FINDS_CATEGORY && products && findsOf(products).length > 0 ? (
        <section
          aria-labelledby="shop-finds-title"
          style={{ marginTop: 'var(--space-8)', paddingTop: 'var(--space-6)', borderTop: 'var(--border-hairline)' }}
        >
          <h2 id="shop-finds-title" className="eyebrow" style={{ marginBottom: 'var(--space-3)' }}>
            {t('finds.teaserEyebrow')}
          </h2>
          <p style={{ maxWidth: 'var(--width-narrow)' }}>{t('finds.teaserText')}</p>
          <Link to={lp(FINDS_PATH)} className="link-underline">
            {t('finds.teaserCta')}
          </Link>
        </section>
      ) : null}

      {/* The shop's editorial text sits below the products. */}
      <div className="prose" style={{ maxWidth: 'var(--width-narrow)', marginTop: 'var(--space-8)', paddingBottom: 'var(--space-8)' }}>
        <Markdown source={t(`shop.${category}.intro`)} />
      </div>

      {/* «En el Edit»: hidden when the category's Edit category has no published article. */}
      {editArticles.length > 0 ? (
        <section
          aria-labelledby="shop-articles-title"
          style={{ paddingTop: 'var(--space-6)', paddingBottom: 'var(--space-8)', borderTop: 'var(--border-hairline)' }}
        >
          <h2 id="shop-articles-title" className="eyebrow" style={{ marginBottom: 'var(--space-5)' }}>
            {t('product.editTitle')}
          </h2>
          <div className="editorial-grid">
            {editArticles.map((raw) => (
              <EditorialCard key={raw.id} article={localizeArticle(raw, locale)} />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
